import { Injectable, inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, from, map, catchError, switchMap, of, throwError, tap } from 'rxjs';
import { AbstractTableToolsPsFormarService, IPsResponseFormat } from './abstract-table-tools-psformart.service';
import { AuthService } from './auth.service';
import { HttpErrorResponse } from '@angular/common/http';
export interface ICustomerRecord {
    id_usuario: number;
    nombre: string;
    descripcion: string;
    password: string;
    activo: 1|0;
    email: string;
    id_alumni: number;
    [key: string]: any;
}

export interface ICustomerLoginRecord {
    id_usuario: number;
    name: string;
    l_roles?: number[];
}

export interface ICustomerLogin extends ICustomerLoginRecord {
    tokens: {
        access: string;
        refresh: string;
    };
}


interface ILogged {
    id: number;
    id_usuario: number;
}

@Injectable({
    providedIn: 'root'
})
export class CustomerService extends AbstractTableToolsPsFormarService {
    // Inyección moderna con inject()
    private router = inject(Router);
    private _AuthS = inject(AuthService);
      
    protected _class_name = 'UsuarioAjax';
    protected _inc_json = 'usuario';


    private userChecked = new Subject<ICustomerLogin>();
    public onUserChecked = this.userChecked.asObservable();

    private readonly ID_ROLE_LOGIN = 1;
    private readonly ID_ROLE_SYSADMIN = 2;
    private readonly ID_ROLE_USRADMIN = 3;
    private readonly ID_ROLE_CONTENTADMIN = 4;

    private readonly SESSION_KEY_LOGGED = 'logged';

    // ===========================================================================
    // Constructor
    // ---------------------------------------------------------------------------
    constructor() {
        super(inject(Injector));  // Solo para la clase base
        this.value = this._getNewEmptyRecord();
    }

    // ===========================================================================
    // Getters y Setters
    // ---------------------------------------------------------------------------
    get id(): number {
        return this.value?.id || 0;
    }

    set id(newId: number) {
        if (this.value) {
            this.value.id = newId;
        }
    }

    // ---------------------------------------------------------------------------
    get name(): string {
        return this.value?.name || '';
    }

    set name(newName: string) {
        if (this.value) {
            this.value.name = newName;
        }
    }

    // ===========================================================================
    // Session Management
    // ---------------------------------------------------------------------------
    get logged_alumni(): number | null {
        const loggedStr = sessionStorage.getItem(this.SESSION_KEY_LOGGED);
        if (loggedStr) {
            try {
                const parsed = JSON.parse(loggedStr);
                this.value = parsed;
                return parseInt(parsed.datos.id_alumni, 10);
            } catch (e) {
                console.error('Error parsing logged data', e);
                return null;
            }
        }
        return null;
    }

    get logged(): number | null {
        const loggedStr = sessionStorage.getItem(this.SESSION_KEY_LOGGED);
        if (loggedStr) {
            try {
                const parsed = JSON.parse(loggedStr);
                this.value = parsed;
                return parseInt(parsed.id_usuario, 10);
            } catch (e) {
                console.error('Error parsing logged data', e);
                return null;
            }
        }
        return null;
    }

    set logged(loggedInfo: any | null) {
        if (!loggedInfo) {
            sessionStorage.removeItem(this.SESSION_KEY_LOGGED);
        } else {
            sessionStorage.setItem(this.SESSION_KEY_LOGGED, JSON.stringify(loggedInfo));
        }
    }

    // ===========================================================================
    // Auth Methods
    // ---------------------------------------------------------------------------
    public canActivate(): Observable<boolean> {
        if (this.logged) {
            return of(true);
        }
        this.submitLogout();
        return of(false);
    }

    // ---------------------------------------------------------------------------
    public submitLogin(loginUrlBase: string, user: string, pwd: string): Observable<ICustomerLogin> {
        return from(this.hashSHA256(pwd)).pipe(
            switchMap((sha256Hash: string) => {
                const data = {
                    username: user,
                    password: sha256Hash
                };

                return this.requestToServerPostJSON('loginJSON', data);
            }),
            tap((customData: ICustomerLogin) => {
                if (!customData?.id_usuario) {
                    throw new Error('Invalid user credentials');
                }
                this.value = customData;
                if (!this.hasRole(this.ID_ROLE_LOGIN)) {
                    throw new Error('User not have permission to login');
                }
                this.logged = customData;
                this.userChecked.next(customData);
                
                // return of(customData);
            }),
            catchError((error:any) => {
                this.logged = null;
                this.resetRecord();
                const msg_error = typeof error==='string'?error:error.message;
                console.error('Login error:', msg_error);
                return throwError(() => msg_error);
            })
        );
    }

    // -------------------------------------------------------------------------
    public sendPwdRecoveryMail(mail_data: any): Observable<any> {
        return this.requestToServerPostJSON('send_password_recovery_mail', mail_data).pipe(
            map((response: any) => {
                return response;
            }),
            catchError((error) => {
                console.error('Error sending password recovery mail:', error);
                return throwError(() => error);
            })
        );
    }

    // -------------------------------------------------------------------------
    public submitLogout(): void {

        this.logged = null;
        this.resetRecord();
        this._AuthS.logout();
        this.router.navigate(['/login']);

    }

    // -------------------------------------------------------------------------
    public readById(loggedData: ILogged): Observable<ICustomerLoginRecord> {
        return this.requestToServerPostJSON('check_customer', loggedData).pipe(
            map((customerData: any) => customerData as ICustomerLoginRecord),
            catchError((error) => {
                console.error('Error reading customer by ID:', error);
                return throwError(() => error);
            })
        );
    }
// -------------------------------------------------------------------------
    public readByNombre(nombre: string): Observable<ICustomerRecord|null> {
        const filter = {    
            filter: {
                nombre: nombre
            }
        };
        return this.requestToServerPostJSON('existeUsuarioPorNombre', filter).pipe(
            map((customerData: IPsResponseFormat|null) => {
                if ( !customerData || !customerData.datos || customerData.datos.length === 0 ) {
                    return null;
                }
                return customerData.datos[0] as ICustomerRecord;
            }),
            catchError((error) => {
                console.error('Error reading customer by nombre:', error);
                return throwError(() => error);
            })
        );
    }

    // -------------------------------------------------------------------------
    public save(customerData: ICustomerRecord): Observable<ICustomerRecord> {
        
        return this.requestToServerPostJSON('registrarUsuario', customerData).pipe(
            map((customerData: ICustomerRecord) => customerData),
            catchError((error: HttpErrorResponse) => {
                console.error('Error saving customer:', error.message);
                return throwError(() => error);
            })
        );

    }

    // ===========================================================================
    // Role Check Methods
    // ---------------------------------------------------------------------------
    public isAdmin(): boolean {
        return this.hasRole(this.ID_ROLE_SYSADMIN);
    }

    // ---------------------------------------------------------------------------
    public isUserAdmin(): boolean {
        return this.hasRole(this.ID_ROLE_USRADMIN);
    }

    // ---------------------------------------------------------------------------
    public isContentAdmin(): boolean {
        return this.hasRole(this.ID_ROLE_CONTENTADMIN);
    }

    // ---------------------------------------------------------------------------
    public isOnlyRead(): boolean {
        return !this.isAdmin() && !this.isContentAdmin();
    }

    // ===========================================================================
    // Protected Methods
    // ---------------------------------------------------------------------------
    protected hasRole(idRole: string | number): boolean {
        if (!this.value?.l_roles) {
            return false;
        }
        
        const rolesArray = Array.isArray(this.value.l_roles) 
            ? this.value.l_roles 
            : Object.values(this.value.l_roles);
        
        return rolesArray.some((role: any) => role == idRole);
    }

    // ---------------------------------------------------------------------------
    protected override _getNewEmptyRecord(): ICustomerLoginRecord | null {
        return null;
    }

    // ===========================================================================
    // Private Helper Methods
    // ---------------------------------------------------------------------------
    public async hashSHA256(message: string): Promise<string> {
        const msgUint8 = new TextEncoder().encode(message);
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    }

    // ---------------------------------------------------------------------
    public getDefaultValues(): ICustomerRecord {
        return {
            id_usuario: 0,
            nombre: '',
            descripcion: '',
            password: '',
            activo: 0,
            email: '',
            id_alumni: 0,
        }
    }
                
}
